import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Brain, ShieldCheck, Sparkle } from "@phosphor-icons/react";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("admin@itsme.ai");
  const [password, setPassword] = useState("Admin@123");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e, mode) => {
    e.preventDefault();
    setBusy(true);
    const res = mode === "login"
      ? await login(email, password)
      : await register({ email, password, name: name || email.split("@")[0], role: "analyst" });
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome to ITSME`);
      nav(loc.state?.from?.pathname || "/", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden"
           style={{ background: "linear-gradient(180deg, #0B0D12 0%, #090A0E 100%)" }}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md grid place-items-center"
               style={{ background: "linear-gradient(135deg, #0055FF, #00E5FF)" }}>
            <Brain size={20} weight="bold" color="#0B0D12" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg tracking-tight">ITSME</div>
            <div className="data-label">Agentic Ops Console</div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            Orchestrate <span style={{ color: "#00E5FF" }}>every process,</span> continuously improve.
          </h1>
          <p className="mt-6 text-slate-400 text-base leading-relaxed">
            Six specialized AI agents ingest your SOPs, logs, and workflows — discovering bottlenecks,
            simulating fixes, and closing the loop from insight to action.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { icon: <Sparkle size={18} />, label: "Continuous Discovery" },
              { icon: <Brain size={18} />, label: "6 Specialized Agents" },
              { icon: <ShieldCheck size={18} />, label: "Enterprise Guardrails" },
            ].map((f, i) => (
              <div key={i} className="itsme-card p-3">
                <div style={{ color: "#00E5FF" }}>{f.icon}</div>
                <div className="text-xs mt-2 text-slate-300">{f.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 data-label">Trusted by ITSM · SRE · Compliance</div>

        <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ backgroundImage: "url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRlY2huaWNhbCUyMGRhdGElMjB2aXN1YWxpemF0aW9uJTIwbmV0d29yayUyMG5vZGUlMjBkYXJrfGVufDB8fHx8MTc4NTQzNTU2MXww&ixlib=rb-4.1.0&q=85)",
                     backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "screen" }} />
      </div>

      <div className="flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-md grid place-items-center"
                 style={{ background: "linear-gradient(135deg, #0055FF, #00E5FF)" }}>
              <Brain size={18} weight="bold" color="#0B0D12" />
            </div>
            <div className="font-heading font-bold text-lg">ITSME</div>
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
          <p className="text-sm text-slate-400 mt-2">Access your Agentic Ops workspace.</p>

          <Tabs defaultValue="login" className="mt-6">
            <TabsList data-testid="auth-tabs" className="bg-[#13151A] border border-[#272A35]">
              <TabsTrigger data-testid="auth-tab-login" value="login">Sign in</TabsTrigger>
              <TabsTrigger data-testid="auth-tab-register" value="register">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={(e) => submit(e, "login")} className="space-y-4 mt-4">
                <div>
                  <Label>Email</Label>
                  <Input data-testid="login-email" type="email" value={email}
                         onChange={(e) => setEmail(e.target.value)} required
                         className="mt-1 bg-[#0F1117] border-[#272A35]" />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input data-testid="login-password" type="password" value={password}
                         onChange={(e) => setPassword(e.target.value)} required
                         className="mt-1 bg-[#0F1117] border-[#272A35]" />
                </div>
                <Button data-testid="login-submit" type="submit" disabled={busy}
                        className="w-full bg-[#0055FF] hover:bg-[#0044cc] text-white">
                  {busy ? "Signing in…" : "Sign in →"}
                </Button>
                <div className="text-xs text-slate-500 pt-2">
                  Demo: <span className="font-mono">admin@itsme.ai / Admin@123</span>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={(e) => submit(e, "register")} className="space-y-4 mt-4">
                <div>
                  <Label>Full name</Label>
                  <Input data-testid="reg-name" value={name} onChange={(e) => setName(e.target.value)}
                         className="mt-1 bg-[#0F1117] border-[#272A35]" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input data-testid="reg-email" type="email" value={email}
                         onChange={(e) => setEmail(e.target.value)} required
                         className="mt-1 bg-[#0F1117] border-[#272A35]" />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input data-testid="reg-password" type="password" value={password}
                         onChange={(e) => setPassword(e.target.value)} required
                         className="mt-1 bg-[#0F1117] border-[#272A35]" />
                </div>
                <Button data-testid="reg-submit" type="submit" disabled={busy}
                        className="w-full bg-[#0055FF] hover:bg-[#0044cc] text-white">
                  {busy ? "Creating…" : "Create account →"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
